// =============================================================================
// Lista członków organizacji — czysta logika ekranu /admin/users.
//
// Do 10.09.2026 ekran łączył `user_organizations` z `users` po kluczu obcym
// i czytał `member.user.email` bez pytania, czy `user` w ogóle jest. Polityka
// RLS na `users` (`users_select_own`) oddaje właścicielowi organizacji
// WYŁĄCZNIE jego własny wiersz — profil każdego innego członka wracał jako
// `null`, pierwsze `.toLowerCase()` rzucało, a React zwijał całe drzewo do
// pustego ekranu. Zmierzone na OGYY (dwóch członków): emulacja zapytania pod
// JWT-em właściciela dała `email: null` dla drugiego konta.
//
// Dwie decyzje:
//  1. Dane przychodzą z RPC `czlonkowie_organizacji` (migracja 679), które
//     oddaje trzy pola profilu członkom TEJ organizacji — zamiast luzować RLS
//     na tabeli niosącej NIP, adres i telefon użytkownika.
//  2. Ekran i tak zakłada, że profilu może NIE BYĆ (członek bez wiersza
//     w `users`, stara odpowiedź z cache). Brak profilu to wiersz z pustym
//     e-mailem, nie wyjątek.
// =============================================================================

/** Wiersz oddawany przez RPC `czlonkowie_organizacji` (679). */
export interface WierszCzlonka {
  id: string;
  user_id: string;
  role: string;
  status: string;
  created_at: string;
  user_email: string | null;
  user_full_name: string | null;
}

/** Kształt, na którym pracuje ekran — jak dawny join, ale `user` zawsze jest. */
export interface OrganizationMember {
  id: string;
  user_id: string;
  role: string;
  status: string;
  created_at: string;
  user: {
    id: string;
    email: string;
    full_name: string | null;
  };
}

/**
 * Wiersze RPC → członkowie ekranu. Profil bez e-maila (brak wiersza w `users`)
 * dostaje pusty łańcuch, żeby żaden `.toLowerCase()` niżej nie miał na czym
 * paść; `user.id` to zawsze `user_id` członkostwa.
 */
export function czlonkowieZOdpowiedzi(wiersze: readonly WierszCzlonka[]): OrganizationMember[] {
  return wiersze.map((w) => ({
    id: w.id,
    user_id: w.user_id,
    role: w.role,
    status: w.status,
    created_at: w.created_at,
    user: {
      id: w.user_id,
      email: typeof w.user_email === 'string' ? w.user_email : '',
      full_name: typeof w.user_full_name === 'string' ? w.user_full_name : null,
    },
  }));
}

/**
 * Filtr wyszukiwarki po e-mailu i nazwie — odporny na brak obu (członek bez
 * profilu zostaje na liście przy pustym zapytaniu i znika przy niepustym).
 */
export function filtrujCzlonkow<
  T extends { user?: { email?: string | null; full_name?: string | null } | null },
>(czlonkowie: readonly T[], zapytanie: string): T[] {
  const q = zapytanie.trim().toLowerCase();
  if (!q) return [...czlonkowie];
  return czlonkowie.filter(
    (m) =>
      (m.user?.email ?? '').toLowerCase().includes(q) ||
      (m.user?.full_name ?? '').toLowerCase().includes(q)
  );
}

/** Inicjał do awatara: pierwsza litera nazwy, potem e-maila, potem „?”. */
export function inicjalCzlonka(
  user: { email?: string | null; full_name?: string | null } | null | undefined
): string {
  const nazwa = user?.full_name?.trim();
  if (nazwa) return nazwa[0].toUpperCase();
  const email = user?.email?.trim();
  if (email) return email[0].toUpperCase();
  return '?';
}

/** Etykieta członka bez profilu — zamiast pustej komórki. */
export function etykietaCzlonka(
  user: { email?: string | null; full_name?: string | null } | null | undefined
): {
  nazwa: string;
  email: string;
} {
  return {
    nazwa: user?.full_name?.trim() || 'Unnamed User',
    email: user?.email?.trim() || '—',
  };
}
